import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Formik, Form } from "formik";
import { flexRender, getCoreRowModel, useReactTable, getSortedRowModel } from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import {
  TableWrapper,
  TableTop,
  TableBottom,
  TableHeader,
  TableBody,
  TableFooter,
  CellInner,
  TableActions,
  TablePagination,
  TableFilters,
  FiltersWrap,
  TableBtns,
  Table,
  TPdivMobileHidden,
  TPdiv,
  NumRecords,
  FiltersBackdrop,
  FiltersHeader,
  FiltersBody,
  FiltersFooter,
  ModalClose,
} from "./styled";

import { ArrowIcon, ChevronIcon, FilterIcon, UsersIcon } from "components/svg";

import SearchField from "components/forms/SearchField";
import SelectInput from "components/forms/SelectInput";
import H5 from "components/typography/H5";
import BasicButton from "components/forms/BasicButton";
import P from "components/typography/P";
import { Box, Button, Flex, IconButton, Spinner } from "theme-ui";
import { findInNestedArray, getNestedValue } from "helpers/default";
import { themevals } from "theme/themevals";

interface FilterProps {
  name: string;
  [key: string]: any;
}

interface Filter {
  key?: string;
  props: FilterProps;
  component: React.ComponentType<any>;
  convertValue?: (value: any) => any;
  updateValue?: (value: any) => any;
  condition?: string;
}

interface RenderFilterParams {
  filter: Filter;
  values: any;
  setFieldValue: (key: string, value: any) => void;
  index: number | string;
}

const TableData = ({
  api,
  apiVariables,
  title,
  refresh,
  columns,
  actions,
  filters,
  preFilters = {},
  filtersStyles,
  searchStyles,
  defaultSortBy = "id",
  defaultSortDir = "asc",
  defaultPageSize = 15,
  pageSizes,
  enableSearch = true,
  enablePaging = true,
  enableFooter = false,
  pagingIfPaged = false,
  nothingFoundText,
  defaultFilters = {},
  customStyles,
  size,
  isFixed,
  returnData,
  returnQuery,
  onFiltersChange,
  stateKey,
  rowStyle,
}: {
  api?: any;
  apiVariables?: { [key: string]: any };
  title?: any;
  refresh?: boolean;
  columns?: any;
  actions?: any[];
  filters?: any[];
  preFilters?: any;
  filtersStyles?: any;
  searchStyles?: any;
  defaultSortBy?: string;
  defaultSortDir?: string;
  defaultPageSize?: number;
  enableSearch?: boolean;
  enablePaging?: boolean;
  enableFooter?: boolean;
  pagingIfPaged?: boolean;
  nothingFoundText?: string;
  pageSizes?: number[];
  defaultFilters?: object;
  customStyles?: any;
  /**
   * Optional namespace for localStorage state persistence. When
   * provided, sort / pagination / filter state is scoped per-page under
   * `tableDataState:<stateKey>` so tables don't stomp each other. When
   * omitted, falls back to the legacy shared `tableDataState` key
   * (existing behavior).
   */
  stateKey?: string;
  size?: "sm" | "lg";
  isFixed?: boolean;
  returnData?: (e?: any) => void;
  returnQuery?: (e?: any) => void;
  onFiltersChange?: (filters: Record<string, string>) => void;
  /** Inline style for a body row, from its data - e.g. tint overdue rows. */
  rowStyle?: (row: any) => React.CSSProperties | undefined;
}) => {
  const LOCAL_STORAGE_KEY = stateKey
    ? `tableDataState:${stateKey}`
    : `tableDataState`;

  const callApi = useRef(api);
  const callReturnData = useRef(returnData);
  const callReturnQuery = useRef(returnQuery);

  const loadTimer = useRef<any>(false);
  const filterFormRef = useRef<any>(false);
  const controller = useRef<any>(false);

  const [loadingData, setLoadingData] = useState<boolean>(true);
  const [tableData, setTableData] = useState<any[]>([]);
  const [sorting, setSorting] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>({});
  const [pageCount, setPageCount] = useState<number>(1);
  const [columnFilters, setColumnFilters] = useState<any>(() => ({ ...(preFilters || {}) }));
  const [globalFilter, setGlobalFilter] = useState<any>(null);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const buildUrlFilters = useCallback(
    (source: Record<string, string>) => {
      const result: Record<string, string> = {};
      if (!source) return result;

      Object.entries(source).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        const config = Array.isArray(filters) ? findInNestedArray(filters, "key", key) : undefined;
        const serialized = typeof config?.serializeValue === "function" ? config.serializeValue(`${value}`) : `${value}`;
        if (serialized !== null && serialized !== undefined && serialized !== "") {
          result[key] = serialized;
        }
      });

      return result;
    },
    [filters]
  );

  const [showRecordsOptions, setShowRecordsOptions] = useState(
    pageSizes
      ? [...pageSizes.map((p) => ({ value: p, label: p })), { value: 1000, label: "All" }]
      : [
          { value: 15, label: 15 },
          { value: 50, label: 50 },
          { value: 100, label: 100 },
          { value: 1000, label: "All" },
        ]
  );

  const fuzzyFilter = (row: any, columnId: any, value: any, addMeta: any) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  const saveStateToLocalStorage = () => {
    if (!(pagination.pageIndex >= 0)) return;

    const state = {
      api: api.name || "",
      timestamp: moment().valueOf(),
      sorting,
      pagination,
      columnFilters,
      globalFilter,
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  };

  useEffect(() => saveStateToLocalStorage(), [sorting, pagination, columnFilters, globalFilter]);

  const preFiltersKey = JSON.stringify(preFilters || {});

  const loadStateFromLocalStorage = () => {
    const savedState = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (savedState) {
      const state = JSON.parse(savedState);
      if (state.timestamp && moment(state.timestamp).isBefore(moment().subtract(12, "hours"))) return;
      if (state.api !== api.name) return;

      setSorting(state.sorting || [{ id: defaultSortBy, desc: defaultSortDir === "desc" }]);
      setPagination(state.pagination || { pageIndex: 0, pageSize: defaultPageSize || 15 });
      const savedFilters = state.columnFilters || {};
      const baseFilters = preFilters || {};
      const mergedFilters: Record<string, any> = { ...savedFilters };
      Object.entries(baseFilters).forEach(([key, value]) => {
        mergedFilters[key] = value;
      });
      setColumnFilters(mergedFilters);
      setGlobalFilter(state.globalFilter || null);
    }

    setShowFilters(false);
  };

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      sorting,
      pagination: {
        pageIndex: pagination?.pageIndex || 0,
        pageSize: pagination?.pageSize || defaultPageSize || 15,
      },
      globalFilter,
    },
    pageCount: pageCount,
    manualSorting: true,
    manualPagination: true,
    onSortingChange: (s) => {
      setSorting(s);
    },
    getSortedRowModel: getSortedRowModel(),
    onGlobalFilterChange: (f) => {
      setGlobalFilter(f);
    },
    onPaginationChange: (p) => {
      setPagination(p);
    },
    globalFilterFn: fuzzyFilter,
  });

  let columnTimer: any;
  const onFormChange = (e: any) => {
    setShowFilters(false);
    if (e) {
      setLoadingData(true);
      const newFilters: Record<string, any> = {};
      for (const [key, rawValue] of Object.entries(e)) {
        const filterConfig = findInNestedArray(filters, "key", key);
        const normalized = typeof filterConfig?.convertValue === "function" ? filterConfig.convertValue(rawValue) : rawValue;

        let stringValue: string;
        if (Array.isArray(normalized)) {
          const tokens = normalized.map((item: any) => (typeof item === "object" && item !== null && "value" in item ? `${item.value}` : `${item}`));
          stringValue = tokens.join(",");
        } else {
          stringValue = normalized ?? "";
        }

        if (stringValue === "" || stringValue === null || typeof stringValue === "undefined") continue;

        if (filterConfig?.condition) {
          stringValue = `${stringValue}:${filterConfig.condition}`;
        }

        newFilters[key] = stringValue;
      }

      if (JSON.stringify(columnFilters) !== JSON.stringify(newFilters)) {
        onFiltersChange?.(buildUrlFilters(newFilters));
        clearTimeout(columnTimer);
        columnTimer = setTimeout(() => {
          table.setPageIndex(0);
          setColumnFilters(newFilters);
        }, 500);
      } else {
        setLoadingData(false);
      }
    }
  };

  let typingTimer: any;
  const onSearchChange = (e: any) => {
    const code = e.keyCode || e.which;
    if (code === 37 || code === 38 || code === 39 || code === 40) {
      return;
    }

    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => {
      table.setPageIndex(0);
      const value = e.target.value;
      if (value.length >= 3 || code === 13) {
        setGlobalFilter(value);
      } else {
        setGlobalFilter(null);
      }
    }, 500);
  };

  const getData = useCallback(async () => {
    if (controller.current) {
      controller.current.abort();
      controller.current = null;
    }
    clearTimeout(loadTimer.current);

    loadTimer.current = setTimeout(async () => {
      setTableData([]);
      setLoadingData(true);
      controller.current = new AbortController();
      const query = {
        filters: {
          ...columnFilters,
          ...(defaultFilters || {}),
        },
        search: globalFilter || null,
        sortBy: sorting[0] ? (sorting[0]["id"] ? sorting[0]["id"] : defaultSortBy) : defaultSortBy || "id",
        sortDir: sorting[0] ? (sorting[0]["desc"] ? "desc" : "asc") : defaultSortDir || "asc",
        pageNum: (pagination.pageIndex || 0) + 1,
        recordsPer: pagination.pageSize || defaultPageSize || 15,
      };

      try {
        const apiCall = callApi.current;
        const res = await apiCall({ ...apiVariables, query, controller: controller.current });
        if (res.items) {
          setTableData(res.items ? res.items : []);
          setPageCount(res.pages || 1);
          setShowRecordsOptions((old) => {
            const all = old.find((i) => i.label === "All");
            if (all) {
              all.value = res.total || all.value;
            }
            return old;
          });
        } else {
          if (Array.isArray(res)) {
            setTableData(res);
            setPageCount(1);
            table.setPageIndex(0);
          }
        }

        const returnDataCall = callReturnData.current;
        if (typeof returnDataCall === "function") {
          returnDataCall(res);
        }

        const returnQueryCall = callReturnQuery.current;
        if (typeof returnQueryCall === "function") {
          returnQueryCall(query);
        }
      } catch (e: any) {}

      setLoadingData(false);
    }, 500);
    return true;
  }, [sorting, pagination.pageIndex, pagination.pageSize, globalFilter, columnFilters]);

  useEffect(() => {
    setColumnFilters((prev: any) => {
      const base = preFilters || {};
      const baseKeys = new Set(Object.keys(base));
      const preserved: any = {};

      Object.entries(prev || {}).forEach(([key, value]) => {
        if (!baseKeys.has(key)) {
          preserved[key] = value;
        }
      });

      const next = { ...preserved, ...base };
      if (JSON.stringify(next) !== JSON.stringify(prev || {})) {
        return next;
      }
      return prev;
    });
    setShowFilters(false);
  }, [preFiltersKey]);

  const appliedFilters = useMemo(() => {
    if (!filters || !Array.isArray(filters)) return [] as Array<{ key: string; label: string; token: string; display: string }>;
    return Object.entries(columnFilters || {}).reduce<Array<{ key: string; label: string; token: string; display: string }>>((acc, [key, rawVal]) => {
      if (rawVal === undefined || rawVal === null || rawVal === "") return acc;
      const config = filters.find((f) => {
        const propName = f?.props?.name;
        if (Array.isArray(propName)) return false;
        return propName === key;
      });

      const baseLabel = config?.props?.label || key;
      const rawString = `${rawVal}`;
      const [valuePart, ...conditionParts] = rawString.split(":");
      const mainValue = valuePart || "";
      if (mainValue === "") return acc;

      const options = config?.props?.options;
      mainValue.split(",").forEach((token) => {
        if (!token) return;
        const match = Array.isArray(options) && options.find((opt: any) => `${opt?.value}` === `${token}`);
        acc.push({
          key,
          label: baseLabel,
          token,
          display: match?.label || token,
        });
      });

      return acc;
    }, []);
  }, [columnFilters, filters]);

  const handleClearFilter = (key: string, token?: string) => {
    setColumnFilters((prev: any) => {
      if (!prev || !(key in prev)) return prev;
      const raw = `${prev[key]}`;
      const [valuePart, ...conditionParts] = raw.split(":");
      const conditionSuffix = conditionParts.length ? `:${conditionParts.join(":")}` : "";

      const next = { ...prev };
      if (!token) {
        delete next[key];
      } else {
        const remaining = (valuePart || "").split(",").filter((item) => item && item !== token);

        if (remaining.length) {
          next[key] = `${remaining.join(",")}${conditionSuffix}`;
        } else {
          delete next[key];
        }
      }

      onFiltersChange?.(buildUrlFilters(next));
      return next;
    });
    table.setPageIndex(0);
  };

  const renderFilter = ({ filter, values, setFieldValue, index }: RenderFilterParams) => {
    const getValue = getNestedValue(values, filter.props.name);
    const resolvedValue = filter?.convertValue ? filter.convertValue(getValue) : getValue;
    const newValue = filter?.props?.multiple
      ? (() => {
          if (Array.isArray(resolvedValue)) return resolvedValue;
          if (typeof resolvedValue === "string") {
            return resolvedValue
              .split(",")
              .map((token) => token.trim())
              .filter(Boolean);
          }
          return [];
        })()
      : resolvedValue;
    const updateValue = (val?: any) => {
      const newVal = filter?.updateValue ? filter.updateValue(val) : val;
      setFieldValue(`${filter.props.name}`, newVal);
    };

    const Component = filter.component;
    console.log("Rendering filter:", filter.key || filter.props.name, JSON.stringify(newValue));
    return <Component key={filter.key || index} {...filter.props} value={newValue} onChange={updateValue} />;
  };

  useEffect(() => {
    if (refresh) {
      getData();
    }
  }, [getData, refresh]);

  useEffect(() => {
    getData();
  }, [getData, columnFilters]);

  useEffect(() => {
    loadStateFromLocalStorage();
  }, []);

  return (
    <>
      {(Array.isArray(filters) || enableSearch || actions) && (
        <>
          <TableTop>
            <Formik key={JSON.stringify(columnFilters)} initialValues={columnFilters} enableReinitialize={true} onSubmit={onFormChange}>
              {({ values, setFieldValue, submitForm, resetForm }) => {
                return (
                  <Form noValidate autoComplete="off" ref={filterFormRef}>
                    <TableActions>
                      {title && (
                        <H5 fontFamily="Rubik" fontSize={"16px"} fontWeight={"500"}>
                          {title}
                        </H5>
                      )}
                      <Flex sx={{ display: "flex", flexWrap: "wrap", gap: "20px", justifyContent: "flex-end", alignItems: "center", marginLeft: "auto" }}>
                        {appliedFilters.length > 0 && (
                          <Flex sx={{ alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                            {appliedFilters.map((chip) => (
                              <Flex
                                key={`${chip.key}-${chip.token}`}
                                sx={{
                                  alignItems: "center",
                                  gap: "6px",
                                  backgroundColor: "#F8F8F8",
                                  border: `1px solid ${themevals.colors.mdgrey}`,
                                  borderRadius: "999px",
                                  padding: "6px 10px",
                                  fontSize: "12px",
                                  fontFamily: "Poppins",
                                  color: themevals.colors.dkgrey,
                                }}
                              >
                                <Box sx={{ fontWeight: 600 }}>{chip.label}:</Box>
                                <Box>{chip.display}</Box>
                                <ModalClose
                                  type="button"
                                  onClick={() => handleClearFilter(chip.key, chip.token)}
                                  style={{
                                    width: "18px",
                                    height: "18px",
                                    minWidth: "18px",
                                    padding: "0",
                                    backgroundSize: "12px",
                                    backgroundPosition: "center",
                                  }}
                                />
                              </Flex>
                            ))}
                          </Flex>
                        )}
                        {(filters?.length || 0) > 0 && (
                          <BasicButton type="button" scheme="secondaryOutline" onClick={() => setShowFilters(!showFilters)}>
                            <FilterIcon fill={themevals.colors.black} />
                            Filters
                          </BasicButton>
                        )}
                        {enableSearch && (
                          <SearchField
                            name="search"
                            value={globalFilter}
                            $customStyles={searchStyles}
                            onKeyUp={(e: any) => {
                              onSearchChange(e);
                            }}
                            width={"250px"}
                            onChange={() => {
                              submitForm();
                            }}
                            onBlur={() => {
                              submitForm();
                            }}
                          />
                        )}
                        {actions && <TableBtns>{actions}</TableBtns>}
                      </Flex>
                    </TableActions>
                    <TableFilters $active={showFilters}>
                      <FiltersBackdrop
                        onClick={() => {
                          resetForm();
                          setShowFilters(false);
                        }}
                      />
                      <FiltersWrap>
                        <FiltersHeader>
                          <Flex sx={{ alignItems: "flex-start", gap: "12px" }}>
                            <Box sx={{ width: "18px", height: "18px" }}>
                              <FilterIcon width="18px" fill={themevals.colors.black} />
                            </Box>
                            <Box>
                              <H5 fontFamily="Rubik" fontSize={"18px"} fontWeight={"500"} margin={0} padding={0}>
                                Filters
                              </H5>
                              <P fontSize="12px" color={themevals.colors.dkgrey} marginBottom="0px" marginTop="0px" padding={0} fontFamily="Poppins">
                                Refine this list without leaving the page.
                              </P>
                            </Box>
                          </Flex>
                          <ModalClose
                            type="button"
                            onClick={() => {
                              resetForm();
                              setShowFilters(false);
                            }}
                          />
                        </FiltersHeader>
        
                        <FiltersBody $customStyles={filtersStyles}>
                          {filters?.map((filter, index) => {
                            const rawValue = values[filter.props.name];
                           

                            // Apply convertValue if it exists to transform the raw value
                            let thisValue = filter?.convertValue ? filter.convertValue(rawValue) : rawValue;
                           

                            let updateValue: (e?: any) => void;
                            if (Array.isArray(filter.props.name)) {
                              thisValue = [values[filter.props.name[0]], values[filter.props.name[1]]];
                              updateValue = (val?: any) => {
                                thisValue = val;
                                console.log(filter.props.name, val);
                                setFieldValue(filter.props.name[0], val[0]);
                                setFieldValue(filter.props.name[1], val[1]);
                              };
                            } else {
                              updateValue = (val?: any) => {
                                thisValue = val;
                                setFieldValue(filter.props.name, val);
                              };
                            }
                            const Component = filter.component;
                            return <Component key={filter.key || index} {...filter.props} value={thisValue} onChange={updateValue} />;
                          })}
                        </FiltersBody>
                        <FiltersFooter>
                          <BasicButton
                            type="button"
                            outline
                            scheme="secondary"
                            onClick={() => {
                              resetForm();
                              setShowFilters(false);
                            }}
                          >
                            Cancel
                          </BasicButton>
                          <BasicButton
                            type="button"
                            onClick={async () => {
                              await submitForm();
                              setShowFilters(false);
                            }}
                            $submitting={loadingData}
                          >
                            Apply Filters
                          </BasicButton>
                        </FiltersFooter>
                      </FiltersWrap>
                    </TableFilters>
                  </Form>
                );
              }}
            </Formik>
          </TableTop>
        </>
      )}
      <TableWrapper $customStyles={customStyles}>
        <Table size={size} $isFixed={isFixed}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header: any) => (
                  <th key={header.id} style={header.column.columnDef.meta?.style}>
                    {header.isPlaceholder ? null : (
                      <CellInner onClick={header.column.columnDef.enableSorting !== false ? header.column.getToggleSortingHandler() : () => false}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: <ArrowIcon direction="up" fill={"var(--theme-ui-colors-dkgrey)"} width="12px" height="auto" ml="10px" />,
                          desc: <ArrowIcon direction="down" fill={"var(--theme-ui-colors-base_500)"} width="12px" height="auto" ml="10px" />,
                        }[header?.column?.getIsSorted() as "asc" | "desc"] ?? <ArrowIcon fill="white" width="0px" height="auto" ml="10px" />}
                      </CellInner>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </TableHeader>
          <TableBody>
            {loadingData && (
              <tr>
                <td colSpan={table.getAllColumns().length}>
                  <Box sx={{ textAlign: "center" }}>
                    <Spinner size={25} strokeWidth={3} title="loading" />
                  </Box>
                </td>
              </tr>
            )}
            {!loadingData && tableData.length === 0 && (
              <tr>
                <td colSpan={table.getAllColumns().length}>{nothingFoundText}</td>
              </tr>
            )}
            {!loadingData &&
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} style={rowStyle ? rowStyle(row.original) : undefined}>
                  {row.getVisibleCells().map((cell: any) => (
                    <td key={cell.id} style={cell.column.columnDef.meta?.style}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
          </TableBody>
          {enableFooter && (
            <TableFooter>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header: any) => (
                    <th key={header.id} style={{ width: `${100 / headerGroup.headers.length}%` }}>
                      {header.isPlaceholder ? null : (
                        <CellInner onClick={header.column.columnDef.enableSorting !== false ? header.column.getToggleSortingHandler() : () => false}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: <ArrowIcon direction="up" fill={"var(--theme-ui-colors-base_500)"} width="12px" height="auto" ml="10px" />,
                            desc: <ArrowIcon direction="down" fill={"var(--theme-ui-colors-base_500)"} width="12px" height="auto" ml="10px" />,
                          }[header?.column?.getIsSorted() as "asc" | "desc"] ?? <ArrowIcon fill="white" width="12px" height="auto" ml="10px" />}
                        </CellInner>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </TableFooter>
          )}
        </Table>
      </TableWrapper>
      <TableBottom>{enablePaging && (!pagingIfPaged || (pagingIfPaged && table.getPageCount() > 1)) && <Pagination {...{ table, pagination, showRecordsOptions, defaultPageSize }} />}</TableBottom>
    </>
  );
};

export default TableData;

const Pagination = ({ table, pagination, defaultPageSize, showRecordsOptions }: any) => (
  <TablePagination>
    <TPdiv>
      <Button type="button" variant="white" className="btn-first" onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
        <ChevronIcon />
        <ChevronIcon />
      </Button>
      <Button type="button" variant="white" className="btn-prev" onClick={() => table.setPageIndex(table.getState().pagination.pageIndex - 1)} disabled={!table.getCanPreviousPage()}>
        <ChevronIcon />
      </Button>
      <Button type="button" variant="white" className="btn-next" onClick={() => table.setPageIndex(table.getState().pagination.pageIndex + 1)} disabled={!table.getCanNextPage()}>
        <ChevronIcon />
      </Button>
      <Button type="button" variant="white" className="btn-last" onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>
        <ChevronIcon />
        <ChevronIcon />
      </Button>
    </TPdiv>
    <TPdivMobileHidden>
      <div className="page-count">
        <Box sx={{ paddingTop: "2px", paddingRight: "5px" }}>Page</Box>
        <SelectInput
          name="pageIndex"
          options={Array.from(Array(table.getPageCount() || 1).keys()).map((v) => ({ value: v, label: v + 1 }))}
          value={pagination.pageIndex || 0}
          onChange={(e: any) => {
            table.setPageIndex(Number(e));
          }}
          $customStyles={{ padding: "0!important", margin: "0!important", minWidth: "auto" }}
        />
        <Box sx={{ paddingTop: "2px", paddingLeft: "5px" }}>of {table.getPageCount() || 1}</Box>
      </div>
    </TPdivMobileHidden>
    <NumRecords>
      <Box sx={{ paddingTop: "2px", paddingRight: "5px" }}>Show</Box>
      <SelectInput
        name="pageSize"
        options={showRecordsOptions}
        value={pagination.pageSize || defaultPageSize || 15}
        onChange={(e: any) => {
          table.setPageIndex(0);
          table.setPageSize(Number(e));
        }}
        $customStyles={{ padding: "0!important", margin: "0!important", minWidth: "auto" }}
      />
    </NumRecords>
  </TablePagination>
);
